'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useMyCIOAuthOptional } from '@/lib/mycio/MyCIOAuthContext'
import { MC, SERIF, SANS } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { countryName } from '@/lib/mycio/copy/countryNames'
import { getMyCIOCopy, getMyCIOLandingCopy, getMyCIOPlans, getMyCIOYourDataCopy, type MyCIOLandingCopy } from '@/lib/mycio/translations'
import { LANDING_CSS } from './styles'
import dynamic from 'next/dynamic'
import { MCP_URL, SAMPLE_PROB, SAMPLE_VARIANTS, type SampleVariantId, type DocType } from './content'
import { COVERAGE_TIER_ORDER, jurisdictionsByTier, type CoverageTierId } from '@/lib/mycio/coverageTiers.generated'

const BandChart = dynamic(() => import('./BandChart'), {
  ssr: false,
  loading: () => <div style={{ height: 248 }} aria-hidden />,
})
import { useReveal } from './hooks'
import Nav from './Nav'
import Hero from './Hero'
import YourDataSection from './YourDataSection'
import InstantHero, { INSTANT_PREFILL_EVENT } from '@/components/mycio/marketing/InstantHero'
import DocModal from './DocModal'
import CaseShelf from './CaseShelf'
import MarketingFooter from './MarketingFooter'
import PricingGrid from '../_shared/PricingGrid'
import { usePricingSection } from '../_shared/usePricingSection'
import { SUPPORT_EMAIL } from '@/lib/mycio/brand'

/**
 * Tier chrome, from the design handoff's coverage spec (README "Tax coverage — tiered depth").
 * Colour carries the same meaning as the plan-health bands: forest = verified, sand = partial,
 * stone = modelled only. Deliberately NOT a red/green pass-fail — an unverified book is not a
 * failure, it is a thing we have not checked, and the copy says which.
 */
const COVERAGE_TIER_TONE: Record<CoverageTierId, { dot: string; chip: string; border: string; name: string }> = {
  full: { dot: '#2F5D4F', chip: '#DDE8E0', border: '#9DBDAE', name: '#21463A' },
  core: { dot: '#A9925F', chip: '#F1ECE0', border: '#D7CFBD', name: '#4A463D' },
  modelled: { dot: '#C4BCAA', chip: '#FCFAF4', border: '#E4DCCB', name: '#6B6557' },
}

const EYEBROW: React.CSSProperties = { fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 18px' }
const H2: React.CSSProperties = { fontFamily: SERIF, fontWeight: 400, fontSize: 46, lineHeight: 1.08, letterSpacing: '-0.02em', textWrap: 'balance' }

// The MCP-connector section is VISIBLE again (founder, 2026-08-15) — but it is SIGNED-IN ONLY to use.
// Anyone may see that the channel exists; only a signed-in user gets the server URL and the setup guide.
// That is not decoration: the connector authenticates the person through our OAuth server, so a URL handed
// to a signed-out visitor cannot do anything for them except fail at the sign-in step inside Claude, which
// is a worse first experience than being asked to sign in here.
const SHOW_MCP_CONNECT = true

// THE CASE SHELF IS HIDDEN until the cases exist (founder, 2026-09-20: "a good section if we have the
// cases — but we do not yet"). The section's claim is that the FPA Challenge households were put through
// the engine and the results published; showing the shelf before that is true would be the exact
// over-promise the rest of the page was rewritten to avoid. Flip when the first case is live.
const SHOW_CASE_SHELF = false

/**
 * Geo-aware sample selection for the SEE-THE-ANSWER panel: a US visitor never sees "ISA", a UK
 * visitor never "401(k)". SSR renders the neutral `generic` set; on hydration we pick from
 * (1) navigator.language region tag (en-US → us, en-GB → uk, any other explicit region → generic),
 * (2) timezone continent fallback (Europe/London → uk, America/* → us, Europe/* → generic),
 * (3) default = us. Card dimensions are identical across sets, so hydration causes no layout shift.
 */
function useSampleVariant(): SampleVariantId {
  const [variant, setVariant] = useState<SampleVariantId>('generic')
  useEffect(() => {
    try {
      const region = (navigator.language || '').split('-')[1]?.toUpperCase()
      if (region === 'US') return setVariant('us')
      if (region === 'GB') return setVariant('uk')
      if (region) return setVariant('generic')
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      if (tz === 'Europe/London') return setVariant('uk')
      if (tz.startsWith('America/')) return setVariant('us')
      if (tz.startsWith('Europe/')) return setVariant('generic')
      setVariant('us')
    } catch {
      setVariant('us')
    }
  }, [])
  return variant
}

/** Funded-probability dial — a 64% semicircular arc. Pure SVG, no library. */
function ProbDial({ prob, bandLabel, caption }: { prob: number; bandLabel: string; caption: string }) {
  const ARC = Math.PI * 50 // semicircle r=50 path length
  return (
    <div style={{ textAlign: 'center' }}>
      <svg viewBox="0 0 120 68" style={{ width: '100%', maxWidth: 210, display: 'block', margin: '0 auto' }} aria-hidden>
        <path d="M10,62 A50,50 0 0 1 110,62" fill="none" stroke="#EAE3D4" strokeWidth={9} strokeLinecap="round" />
        <path d="M10,62 A50,50 0 0 1 110,62" fill="none" stroke={MC.clay} strokeWidth={9} strokeLinecap="round" strokeDasharray={`${(ARC * prob) / 100} ${ARC}`} />
        <text x="60" y="52" textAnchor="middle" style={{ fontFamily: SERIF, fontSize: 30, fill: MC.ink }}>{prob}%</text>
      </svg>
      <div style={{ marginTop: 4 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: MC.clay, background: MC.clayBg, padding: '5px 12px', borderRadius: 999 }}>{bandLabel}</span>
      </div>
      <p style={{ fontSize: 13, color: MC.muted2, margin: '10px 0 0', lineHeight: 1.4 }}>{caption}</p>
    </div>
  )
}

/** One decision-menu option card. A card without `d` is the probability card (64% ⇒ 84%). */
function OptionCard({ option, compact = false }: { option: { t: string; d?: string }; compact?: boolean }) {
  const isProb = !option.d
  return (
    <div style={{ background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 12, padding: compact ? '10px 12px' : '16px 18px', minHeight: compact ? 0 : 96, display: 'flex', flexDirection: 'column', gap: compact ? 6 : 10 }}>
      <p style={{ fontSize: compact ? 12.5 : 15, fontWeight: 600, color: MC.ink, margin: 0, lineHeight: 1.35 }}>{option.t}</p>
      {isProb ? (
        <>
          <div style={{ position: 'relative', height: 7, borderRadius: 999, background: '#EAE3D4', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${SAMPLE_PROB.to}%`, background: MC.sage, borderRadius: 999 }} />
            <div style={{ position: 'absolute', inset: 0, width: `${SAMPLE_PROB.from}%`, background: MC.forest, borderRadius: 999 }} />
          </div>
          <p style={{ fontSize: compact ? 12 : 13.5, margin: 0, color: MC.muted }}>
            {SAMPLE_PROB.from}% <span style={{ color: MC.forest, fontWeight: 700 }}>⇒ {SAMPLE_PROB.to}%</span>
          </p>
        </>
      ) : (
        <>
          <div style={{ height: 7, borderRadius: 999, background: '#EAE3D4', overflow: 'hidden' }}>
            <div style={{ width: '58%', height: '100%', borderRadius: 999, background: MC.forest }} />
          </div>
          <p style={{ fontSize: compact ? 12 : 13.5, margin: 0, color: MC.forest, fontWeight: 700 }}>{option.d}</p>
        </>
      )}
    </div>
  )
}

export default function LandingPage({ showInstant = false }: { showInstant?: boolean }) {
  const rootRef = useReveal<HTMLDivElement>()
  const router = useRouter()
  const { language } = useMyCIOLanguage()
  const copy = getMyCIOCopy(language)
  const landing = getMyCIOLandingCopy(language)
  const yourData = getMyCIOYourDataCopy(language)
  const variant = useSampleVariant()
  const sampleSet = landing.answer.sets[variant]
  const sampleData = SAMPLE_VARIANTS[variant]
  // Plans + checkout come from the shared hook, so this page and /mycio/pricing cannot drift into showing
  // different tiers, prices or checkout behaviour.
  const plansCopy = getMyCIOPlans(language)
  const fallbackPlansCopy = getMyCIOPlans('en')
  // PayPal pay-by-card left this hook when billing moved to Paddle (2026-09) and PricingGrid dropped
  // the props with it, so the restored page uses the current checkout path rather than reviving a
  // dialog whose rail is gone.
  const {
    plans: localizedPlans, startCheckout, checkoutBusy, checkoutError,
  } = usePricingSection()
  const [openFaq, setOpenFaq] = useState<number>(0)
  const [docOpen, setDocOpen] = useState<DocType | null>(null)
  const [copied, setCopied] = useState(false)
  // MyCIO auth, NOT the legacy hook. `@/hooks/useAuth` reads a `supreme_session` key that
  // nothing under /mycio writes, against a client configured with persistSession:false on this
  // host — so it reports signed-OUT for every MyCIO visitor, and signed-IN for a legacy
  // trading session that has no MyCIO account (review 2026-08-15). MyCIO sessions live under the
  // 'mycio-auth' GoTrue key, which is what this context reads.
  // Optional variant: this component also renders outside the /mycio auth shell.
  const mycioAuth = useMyCIOAuthOptional()
  const authLoading = mycioAuth?.loading ?? true
  // `loading` counts as locked so the server URL never flashes for a signed-out visitor.
  const connectUnlocked = Boolean(mycioAuth?.user) && !authLoading

  // Deep-linkable legal docs: mycio.ai/#terms, /#privacy, /#refunds, /#disclosures, /#risk open the matching
  // modal directly (shareable URLs, no separate routes needed since DocModal already renders the full text).
  // 'refunds' (plural, matches how people write the URL) maps to the 'refund' DocType key.
  useEffect(() => {
    const HASH_TO_DOC: Record<string, DocType> = {
      terms: 'terms', privacy: 'privacy', disclosures: 'disclosures', risk: 'risk', refunds: 'refund', refund: 'refund',
    }
    const syncFromHash = () => {
      const h = window.location.hash.replace('#', '')
      const doc = HASH_TO_DOC[h]
      if (doc) setDocOpen(doc)
    }
    syncFromHash()
    window.addEventListener('hashchange', syncFromHash)
    return () => window.removeEventListener('hashchange', syncFromHash)
  }, [])
  const closeDoc = () => {
    setDocOpen(null)
    if (window.location.hash) history.replaceState(null, '', window.location.pathname + window.location.search)
  }
  const copyMcp = () => {
    try {
      navigator.clipboard?.writeText(MCP_URL)
    } catch {
      /* clipboard may be unavailable */
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  // A decision door: prefill the anonymous hero input with the question and scroll to it.
  const askDoor = (question: string) => {
    window.dispatchEvent(new CustomEvent<string>(INSTANT_PREFILL_EVENT, { detail: question }))
    document.getElementById('try')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Balance-sheet row labels: cash/home/mortgage are shared; the three account rows come from
  // the geo-aware sample set so a US visitor sees 401(k)/Roth and a UK visitor SIPP/ISA.
  const balanceLabels: Record<string, string> = {
    cash: landing.drives.rowCash,
    acct1: sampleSet.rows[0],
    acct2: sampleSet.rows[1],
    acct3: sampleSet.rows[2],
    home: landing.drives.rowHome,
    mortgage: landing.drives.rowMortgage,
  }
  const balanceDots: Record<string, string> = {
    cash: MC.sage, acct1: MC.forest, acct2: MC.forestDeep, acct3: MC.gold, home: '#B98C66', mortgage: MC.clay,
  }
  const flowLabels: Record<string, string> = {
    income: landing.drives.flowIncome,
    tax: landing.drives.flowTax,
    spending: landing.drives.flowSpending,
    savings: landing.drives.flowSavings,
  }
  const flowColors: Record<string, string> = { income: MC.forest, tax: MC.clay, spending: '#B98C66', savings: MC.sage }
  const incomeNum = sampleData.cashflow[0].num

  return (
    <div
      ref={rootRef}
      className="lp"
      style={{ fontFamily: SANS, color: MC.ink, background: MC.bg, overflowX: 'hidden', WebkitFontSmoothing: 'antialiased' }}
    >
      <style>{LANDING_CSS}</style>

      <Nav />
      {/* 72px spacer compensates for the fixed header so content starts below it */}
      <div style={{ height: 72 }} aria-hidden />

      {/* ===== 1 · ASK — the hero + the anonymous one-sentence input (InstantHero, kept as-is). ===== */}
      <Hero />
      {showInstant && (
        <section id="how" className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '40px 32px 96px', scrollMarginTop: 88 }}>
          <p data-reveal style={{ fontSize: 18, lineHeight: 1.6, color: MC.muted, margin: '0 0 36px', maxWidth: 640, textWrap: 'pretty' }}>
            {landing.askLead}
          </p>
          <InstantHero />
        </section>
      )}

      {/* ===== 2 · SEE THE ANSWER — the static sample-output panel (honestly labeled example).
           The DECISION MENU is the product: computed trade-offs, you choose. No glide-path chart. ===== */}
      <section id="why" style={{ background: '#EEE8DC', borderTop: `1px solid ${MC.border}`, borderBottom: `1px solid ${MC.border}` }}>
        <div className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '104px 32px' }}>
          {/* No "SEE THE ANSWER" eyebrow: set in button-like small caps it read as a control and was
              not one (founder, 2026-09-20). The heading carries the section on its own. */}
          <h2 className="mc-h2" style={{ ...H2, margin: '0 0 40px', maxWidth: 720 }} data-reveal>
            {landing.answer.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{landing.answer.titleEm}</em>{landing.answer.titleAfter}
          </h2>

          <div data-reveal style={{ background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 60px -36px rgba(35,33,28,0.4)' }}>
            {/* Honest example label */}
            <div style={{ padding: '18px 26px', borderBottom: '1px solid #ECE5D7', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <p style={{ fontFamily: SERIF, fontSize: 19, color: MC.ink, margin: 0 }}>{sampleSet.example}</p>
            </div>

            <div className="mc-gap48" style={{ display: 'flex', gap: 40, flexWrap: 'wrap', padding: '28px 26px', alignItems: 'center' }}>
              {/* (a) funded-probability dial + (c) binding-constraint chip */}
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <ProbDial prob={SAMPLE_PROB.from} bandLabel={copy.planCard.atRisk} caption={landing.answer.probCaption} />
                <p style={{ fontSize: 14, margin: '18px auto 0', maxWidth: 260, textAlign: 'center', padding: '10px 14px', background: '#F4EFE4', borderRadius: 10, color: MC.muted }}>
                  <strong style={{ color: MC.ink }}>{landing.whatIf.constraintLabel}</strong> {landing.answer.bindingValue}
                </p>
              </div>
              {/* (b) downside/median/upside band */}
              <div style={{ flex: '2 1 320px', minWidth: 280 }}>
                <BandChart
                  projection={SAMPLE_VARIANTS[variant].projection}
                  labels={{
                    upside: landing.answer.upside,
                    median: landing.answer.median,
                    downside: landing.answer.downside,
                    caption: landing.answer.chartCaption,
                    ageAxis: landing.answer.ageAxis ?? 'Age',
                  }}
                />
              </div>
            </div>

            {/* (d) THE DECISION MENU — the optimizer claim + three computed option cards */}
            <div style={{ padding: '26px 26px 24px', borderTop: '1px solid #ECE5D7', background: MC.surfaceAlt }}>
              <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 25, margin: '0 0 8px', color: MC.ink, textWrap: 'balance' }}>{landing.answer.menuTitle}</h3>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: MC.muted, margin: '0 0 18px', maxWidth: 760, textWrap: 'pretty' }}>{landing.answer.menuBody}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                {sampleSet.options.map((o) => (
                  <OptionCard key={o.t} option={o} />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                <p style={{ fontSize: 14.5, fontWeight: 600, color: MC.forest, margin: 0 }}>{landing.answer.menuFooter}</p>
                <p style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: MC.faint2, margin: 0, letterSpacing: '0.02em' }}>{landing.answer.illustrative}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2b · THE CASES — the claim above is ours; this is the evidence for it.
           The sample plan makes the claim on a household we chose. These are households set by the
           body that certifies financial planners, published years before this product existed and
           linked in full, so the reader can check the facts were not picked to flatter us. Placed
           immediately after the sample for that reason: claim, then proof, then depth. ===== */}
      {SHOW_CASE_SHELF && (
      <section className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '0 32px 104px' }}>
        <p style={EYEBROW} data-reveal>Cases we didn’t write</p>
        <h2 className="mc-h2" style={{ ...H2, margin: '0 0 16px', maxWidth: 760 }} data-reveal>
          The cases set for the people who <em style={{ fontStyle: 'italic', color: MC.forest }}>certify</em> financial planners.
        </h2>
        <p style={{ fontSize: 18, lineHeight: 1.6, color: MC.muted, margin: '0 0 40px', maxWidth: 640, textWrap: 'pretty' }} data-reveal>
          Every year the Financial Planning Association publishes a case for the students competing
          in its Financial Planning Challenge — a real household, with the contradictions left in.
          We put each one through the engine. Read the case, then read what came out.
        </p>
        <div data-reveal>
          <CaseShelf />
        </div>
      </section>
      )}

      {/* ===== 3 · WHAT DRIVES IT — the living balance sheet + cashflow reconciliation. ===== */}
      <section className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '104px 32px' }}>
        <p style={EYEBROW} data-reveal>{landing.drives.eyebrow}</p>
        <h2 className="mc-h2" style={{ ...H2, margin: '0 0 16px', maxWidth: 700 }} data-reveal>
          {landing.drives.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{landing.drives.titleEm}</em>{landing.drives.titleAfter}
        </h2>
        <p style={{ fontSize: 18, lineHeight: 1.6, color: MC.muted, margin: '0 0 44px', maxWidth: 620, textWrap: 'pretty' }} data-reveal>
          {landing.drives.body}
        </p>

        <div className="mc-gap48" style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Left: compact balance-sheet card */}
          <div data-reveal style={{ flex: '1 1 340px', minWidth: 300, background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 14, padding: '22px 24px' }}>
            <p style={{ fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 14px' }}>{landing.drives.balanceTitle}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sampleData.balance.map((row) => (
                <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 9, borderBottom: '1px solid #EFE9DC' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: balanceDots[row.id], flex: 'none' }} />
                  <span style={{ fontSize: 14.5, color: MC.inkSoft, flex: 1 }}>{balanceLabels[row.id]}</span>
                  <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13.5, color: row.num < 0 ? MC.clay : MC.ink }}>{row.value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: MC.ink, flex: 1 }}>{landing.drives.netLabel}</span>
                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14.5, fontWeight: 700, color: MC.forest }}>{sampleData.net}</span>
              </div>
            </div>
          </div>

          {/* Right: cashflow reconciliation strip — the bars visibly reconcile to zero */}
          <div data-reveal style={{ flex: '1 1 340px', minWidth: 300, background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 14, padding: '22px 24px', display: 'flex', flexDirection: 'column' }}>
            <p style={{ fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 14px' }}>{landing.drives.cashflowTitle}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
              {sampleData.cashflow.map((flow) => {
                const width = Math.round((Math.abs(flow.num) / incomeNum) * 100)
                return (
                  <div key={flow.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 14, color: MC.inkSoft }}>{flowLabels[flow.id]}</span>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, color: flow.num < 0 ? MC.clay : MC.forest }}>{flow.value}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: '#EAE3D4', overflow: 'hidden' }}>
                      <div style={{ width: `${width}%`, height: '100%', borderRadius: 999, background: flowColors[flow.id] }} />
                    </div>
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, paddingTop: 12, borderTop: '1px solid #EFE9DC' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: MC.ink }}>{landing.drives.reconLabel}</span>
              <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13.5, fontWeight: 700, color: MC.forest }}>0</span>
            </div>
          </div>
        </div>

        <p data-reveal style={{ fontSize: 15.5, color: MC.muted, margin: '28px 0 0', fontStyle: 'italic' }}>{landing.drives.caption}</p>

        {/* The trust throughline, folded into ONE compact row of three badges. */}
        <div data-reveal style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
          {[landing.trust[6], landing.capabilities.cards[1].t, landing.trust[1]].map((badge) => (
            <span key={badge} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: MC.forest, background: MC.sageBg, border: `1px solid ${MC.mist}`, borderRadius: 999, padding: '8px 16px' }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: MC.forest }} />
              {badge}
            </span>
          ))}
        </div>

        {/* Data-trust deep dive collapses into progressive disclosure (nothing moved to a new route). */}
        <details data-reveal style={{ marginTop: 28, border: `1px solid ${MC.border}`, borderRadius: 12, background: MC.surface }}>
          <summary style={{ cursor: 'pointer', padding: '16px 20px', fontSize: 15.5, fontWeight: 600, color: MC.forest, listStyle: 'revert' }}>
            {landing.drives.dataDetails}
          </summary>
          <YourDataSection showInstant={showInstant} />
        </details>
      </section>

      {/* ===== CONNECT / SETUP (hidden while the on-site assistant is the front door — see SHOW_MCP_CONNECT) ===== */}
      {SHOW_MCP_CONNECT && (
      <section id="connect" style={{ background: MC.forestDeep, color: MC.sageBg, borderTop: '1px solid #173329' }}>
        <div className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '104px 32px' }}>
          <p style={{ ...EYEBROW, color: MC.sage }} data-reveal>{landing.connect.eyebrow}</p>
          <h2 className="mc-h2" style={{ ...H2, margin: '0 0 18px', maxWidth: 640, color: MC.bg }} data-reveal>
            {landing.connect.titleBefore}<em style={{ fontStyle: 'italic', color: MC.sage }}>{landing.connect.titleEm}</em>{landing.connect.titleAfter}
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: MC.mist, margin: '0 0 36px', maxWidth: 540, textWrap: 'pretty' }} data-reveal>
            {landing.connect.body}
          </p>

          {/*
            THE TWO PLATFORM CARDS — the design handoff's connect section (README §4): one card per AI,
            numbered steps, a plan/availability note under a hairline. The copy has been in
            `landing.connect.cards` in every language all along; the repositioned page stopped rendering
            it, which is why the section read as a URL box with no "how". Shown to everyone: the steps
            contain no server URL, so they hand nothing over — the URL and the guide stay behind sign-in.
          */}
          <div className="mc-connect-grid" data-reveal data-stagger style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 36 }}>
            {landing.connect.cards.map((card) => (
              <div key={card.name} className="mc-connectcard" style={{ background: MC.surface, color: MC.ink, borderRadius: 16, padding: '26px 28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <span aria-hidden style={{ width: 11, height: 11, borderRadius: 3, background: card.dot, display: 'inline-block' }} />
                  <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 23, margin: 0, color: MC.ink }}>{card.name}</h3>
                </div>
                <ol style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: 0, padding: 0, listStyle: 'none' }}>
                  {card.steps.map((stepText, i) => (
                    <li key={stepText} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                      <span style={{ flex: 'none', width: 26, height: 26, borderRadius: 999, background: '#EEE8DC', color: MC.forest, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                      <p style={{ fontSize: 15.5, lineHeight: 1.45, color: MC.inkSoft, margin: 0, paddingTop: 2, textWrap: 'pretty' }}>{stepText}</p>
                    </li>
                  ))}
                </ol>
                <p style={{ fontSize: 13, lineHeight: 1.45, color: MC.faint, margin: '18px 0 0', paddingTop: 14, borderTop: '1px solid #EBE4D5', textWrap: 'pretty' }}>{card.note}</p>
              </div>
            ))}
          </div>

          {/* SIGNED-OUT: the channel is described, never handed over. No server URL, no guide link. */}
          {!connectUnlocked && (
            <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(157,189,174,0.3)', borderRadius: 12, padding: '18px 20px', marginBottom: 40, maxWidth: 620 }}>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: MC.mist, margin: '0 0 16px' }}>
                {landing.connect.signedOutBody}
              </p>
              <Link href="/sign-in?next=/connect" className="mc-cta-light" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15.5, fontWeight: 600, color: MC.forestDeep, background: '#DDE8E0', padding: '12px 22px', borderRadius: 999, textDecoration: 'none' }}>
                {copy.nav.signin}
              </Link>
            </div>
          )}
          {connectUnlocked && (
          <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(157,189,174,0.3)', borderRadius: 12, padding: '16px 18px', marginBottom: 40, maxWidth: 620 }}>
            <p style={{ fontSize: 11.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: MC.sage, fontWeight: 600, margin: '0 0 9px' }}>{landing.connect.urlLabel}</p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ flex: 1, minWidth: 200, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 15, color: MC.bg, background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(157,189,174,0.25)', borderRadius: 8, padding: '11px 14px' }}>
                {MCP_URL}
              </code>
              <button
                type="button"
                onClick={copyMcp}
                className="mc-cta-light"
                style={{ fontSize: 14, fontWeight: 600, color: MC.forestDeep, background: '#DDE8E0', border: 'none', padding: '11px 18px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {copied ? `${copy.common.copied} ✓` : copy.common.copy}
              </button>
            </div>
          </div>
          )}
          {connectUnlocked && (
          <Link href="/connect" className="mc-cta-light" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: MC.forestDeep, background: '#DDE8E0', padding: '14px 26px', borderRadius: 999, textDecoration: 'none' }}>
            {landing.connect.guideCta}
          </Link>
          )}
        </div>
      </section>
      )}

      {/* ===== 4 · COMPARE BETTER CHOICES — planner framing line and decision-space grid. ===== */}
      <section style={{ background: '#EEE8DC', borderTop: `1px solid ${MC.border}`, borderBottom: `1px solid ${MC.border}` }}>
        <div className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '104px 32px' }}>
          <p style={EYEBROW} data-reveal>{landing.whatIf.eyebrow}</p>
          <h2 className="mc-h2" style={{ ...H2, margin: '0 0 20px', maxWidth: 680 }} data-reveal>
            {landing.whatIf.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{landing.whatIf.titleEm}</em>{landing.whatIf.titleAfter}
          </h2>
          {/* The founder's framing line: every angle a great planner checks — within the tax laws. */}
          <p data-reveal style={{ fontFamily: SERIF, fontSize: 23, lineHeight: 1.4, fontStyle: 'italic', color: MC.forest, margin: '0 0 44px', maxWidth: 760, textWrap: 'pretty' }}>
            {landing.decisionSpace.lead}
          </p>

          {/* Decision-space grid — six groups of real engine decisions, chips only. */}
          <h3 data-reveal style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 27, margin: '0 0 22px', color: MC.ink }}>{landing.decisionSpace.title}</h3>
          <div data-reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 0, borderTop: '1px solid #D7CFBD', borderLeft: '1px solid #D7CFBD', marginBottom: 18 }}>
            {landing.decisionSpace.groups.map((group) => (
              <div key={group.t} style={{ padding: '24px 26px', borderBottom: '1px solid #D7CFBD', borderRight: '1px solid #D7CFBD' }}>
                <p style={{ fontSize: 12.5, color: MC.clay, fontWeight: 600, margin: '0 0 12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{group.t}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {group.chips.map((chip) => (
                    <span key={chip} style={{ fontSize: 13, color: '#3C382F', background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 999, padding: '6px 12px' }}>{chip}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p data-reveal style={{ fontSize: 15, color: MC.muted2, margin: '0 0 44px', maxWidth: 720, textWrap: 'pretty' }}>{landing.decisionSpace.cap}</p>

          {landing.whatIf.asks.length > 0 && (
            <div data-reveal style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {landing.whatIf.asks.map((ask) =>
                showInstant ? (
                  <button
                    key={ask}
                    type="button"
                    onClick={() => askDoor(ask)}
                    className="mc-chip"
                    style={{ fontSize: 15.5, fontWeight: 600, color: MC.forest, background: MC.surface, border: `1px solid ${MC.forest}`, borderRadius: 999, padding: '12px 20px', cursor: 'pointer' }}
                  >
                    “{ask}”
                  </button>
                ) : (
                  <Link key={ask} href="/sign-in#signup" className="mc-chip" style={{ fontSize: 15.5, fontWeight: 600, color: MC.forest, background: MC.surface, border: `1px solid ${MC.forest}`, borderRadius: 999, padding: '12px 20px', textDecoration: 'none' }}>
                    “{ask}”
                  </Link>
                ),
              )}
            </div>
          )}
        </div>
      </section>

      {/* ===== 5 · KEEP IT ALIVE — tax laws change · your life changes · markets move. ===== */}
      <section style={{ background: MC.forestDeep, color: MC.sageBg }}>
        <div className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '104px 32px' }}>
          <p style={{ ...EYEBROW, color: MC.sage }} data-reveal>{landing.living.eyebrow}</p>
          <h2 className="mc-h2" style={{ ...H2, margin: '0 0 56px', maxWidth: 720, color: MC.bg }} data-reveal>
            {landing.alive.titleBefore}<em style={{ fontStyle: 'italic', color: MC.sage }}>{landing.alive.titleEm}</em>{landing.alive.titleAfter}
          </h2>
          <div data-reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 44 }}>
            {landing.alive.vectors.map((v) => (
              <div key={v.t}>
                <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 25, margin: '0 0 12px', color: MC.bg }}>{v.t}</h3>
                <p style={{ fontSize: 16.5, lineHeight: 1.6, color: MC.mist, margin: 0, textWrap: 'pretty' }}>{v.p}</p>
              </div>
            ))}
          </div>
          <p data-reveal style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 21, color: MC.sage, margin: '48px 0 0', textWrap: 'pretty' }}>
            {landing.alive.recheck}
          </p>
        </div>
      </section>

      {/*
        Coverage — THREE LABELLED TIERS, not one flag wall.

        A single undifferentiated row said every jurisdiction was equally supported, which is false:
        only the US and UK are validated by worked cases, eight more are provisional launch markets
        with named open gaps, and the rest are books with no validation pass. The handoff's own rule
        is "Depth is not uniform, so we show it", and the product honours it by naming the tier on
        every computed result. The membership comes from `coverageTiers.generated.ts`, which is
        generated from the engine's launch registry — so a country cannot appear here as verified
        unless `aicio/validation_status.py` says it is.
      */}
      <section id="coverage" className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 32px 64px' }}>
        <div data-reveal style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: 0, color: MC.ink }}>{landing.tax.coverageTitle}</h3>
          <span style={{ fontSize: 13, color: MC.faint }}>{landing.tax.coverageSubtitle}</span>
        </div>
        {COVERAGE_TIER_ORDER.map((tierId) => {
          const members = jurisdictionsByTier(tierId)
          if (!members.length) return null
          const tone = COVERAGE_TIER_TONE[tierId]
          // The two verified tiers reuse the labels the copy already carries in every language;
          // only `modelled` needed a new pair.
          const label = tierId === 'full' ? { label: landing.tax.validatedLabel, blurb: landing.tax.validatedNote }
            : tierId === 'core' ? { label: landing.tax.provisionalLabel, blurb: landing.tax.provisionalNote }
            : { label: landing.tax.modelledLabel, blurb: landing.tax.modelledNote }
          return (
            <div key={tierId} data-reveal style={{ marginBottom: 26 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap', marginBottom: 10 }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: tone.dot, display: 'inline-block' }} />
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tone.name }}>
                  {label.label}
                </span>
                <span style={{ fontSize: 12, color: MC.faint }}>{members.length}</span>
                <span style={{ fontSize: 13, color: MC.muted, textWrap: 'pretty' }}>{label.blurb}</span>
              </div>
              {/* Its own reveal target, so the chips stagger in after the tier label above them. */}
              <div data-reveal data-stagger style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
                {members.map((c) => (
                  /* A chip is a link, not a badge (founder, 2026-09-20: "the buttons animate but you cannot click
                     them"). The ten launch countries have their own page; a modelled country has no page yet, so
                     it goes to the coverage index rather than to a 404. */
                  <Link
                    key={c.code}
                    href={c.tier === 'modelled' ? '/coverage' : `/coverage/${c.flag}`}
                    className="mc-flagchip"
                    aria-label={`${countryName(c.code, language, c.name)} — ${label.label}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 15px', background: tone.chip, border: `1px solid ${tone.border}`, borderRadius: 999, textDecoration: 'none', cursor: 'pointer' }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={`https://flagcdn.com/w80/${c.flag}.png`} alt="" loading="lazy" style={{ width: 24, height: 'auto', borderRadius: 3, display: 'block', boxShadow: '0 0 0 1px rgba(35,33,28,0.06)' }} />
                    <span style={{ fontSize: 14, color: tone.name, whiteSpace: 'nowrap' }}>{countryName(c.code, language, c.name)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )
        })}
        <p data-reveal style={{ fontSize: 13, color: MC.faint, margin: '18px 0 0', maxWidth: 760, textWrap: 'pretty' }}>
          {landing.tax.estimateNote}{' '}
          <Link href="/coverage" style={{ color: MC.forest, textDecoration: 'underline', textUnderlineOffset: 3 }}>
            {landing.tax.detailCta}
          </Link>
        </p>
      </section>

      {/* ===== FAQ (already progressive disclosure — kept) ===== */}
      <section style={{ background: '#EEE8DC', borderTop: `1px solid ${MC.border}`, borderBottom: `1px solid ${MC.border}` }}>
        <div className="mc-container mc-pad-faq" style={{ maxWidth: 920, margin: '0 auto', padding: '96px 32px' }}>
          <h2 className="mc-h2" style={{ ...H2, margin: '0 0 48px' }} data-reveal>
            {landing.faq.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{landing.faq.titleEm}</em>{landing.faq.titleAfter}
          </h2>
          <div data-reveal>
            {landing.faq.items.map((faq, i) => {
              const open = openFaq === i
              return (
                <div key={faq.q} style={{ borderTop: `1px solid ${MC.border}` }}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'center', padding: '26px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: SERIF, fontSize: 24, fontWeight: 500, color: MC.ink }}
                  >
                    <span>{faq.q}</span>
                    <span style={{ flex: 'none', width: 30, height: 30, borderRadius: 999, border: '1px solid #C9C2B2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: SANS, fontSize: 19, color: MC.forest, lineHeight: 1 }}>
                      {open ? '–' : '+'}
                    </span>
                  </button>
                  {open && (
                    <div style={{ padding: '0 56px 30px 0', maxWidth: 760, fontSize: 17.5, lineHeight: 1.66, color: MC.muted, textWrap: 'pretty' }}>{faq.a}</div>
                  )}
                </div>
              )
            })}
            <div style={{ borderTop: `1px solid ${MC.border}` }} />
          </div>
        </div>
      </section>

      {/* ===== PRICING (tier names unchanged — billing-coupled; outcome one-liners live in blurbs) ===== */}
      <PricingGrid
        plans={localizedPlans}
        popularLabel={copy.common.popular}
        checkoutStartingLabel={copy.common.checkoutStarting}
        titleBefore={landing.pricing.titleBefore}
        titleEm={landing.pricing.titleEm}
        titleAfter={landing.pricing.titleAfter}
        body={landing.pricing.body}
        startCheckout={startCheckout}
        checkoutBusy={checkoutBusy}
        checkoutError={checkoutError}
        renewalTitle={plansCopy.renewalTitle ?? fallbackPlansCopy.renewalTitle}
        renewalBody={plansCopy.renewalBody ?? fallbackPlansCopy.renewalBody}
        posture={plansCopy.posture ?? fallbackPlansCopy.posture}
      />

      {/* ===== FINAL CTA — back to the anonymous hero input (signup is asked AFTER the result). ===== */}
      <section data-reveal className="mc-container mc-pad-finalcta" style={{ maxWidth: 1180, margin: '0 auto', padding: '124px 32px', textAlign: 'center' }}>
        <h2 className="mc-finalcta-h" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 56, lineHeight: 1.05, letterSpacing: '-0.025em', margin: '0 0 18px', textWrap: 'balance' }}>
          {landing.finalCta.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{landing.finalCta.titleEm}</em>{landing.finalCta.titleAfter}
        </h2>
        <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 22, color: MC.forest, margin: '0 0 16px' }}>{landing.finalCta.sub}</p>
        <p style={{ fontSize: 19, lineHeight: 1.55, color: MC.muted, margin: '0 auto 36px', maxWidth: 540, textWrap: 'pretty' }}>
          {landing.finalCta.body}
        </p>
        {showInstant ? (
          <a href="#how" className="mc-cta-primary" style={{ display: 'inline-block', fontSize: 16.5, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '16px 32px', borderRadius: 999, textDecoration: 'none' }}>
            {landing.finalCtaAsk}
          </a>
        ) : (
          <Link href="/sign-in#signup" className="mc-cta-primary" style={{ display: 'inline-block', fontSize: 16.5, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '16px 32px', borderRadius: 999, textDecoration: 'none' }}>
            {copy.hero.primaryCta}
          </Link>
        )}
        <p style={{ fontSize: 13.5, color: MC.faint, margin: '22px 0 0', letterSpacing: '0.01em' }}>{copy.hero.micro}</p>
      </section>

      {/* ===== FOOTER (legal disclosures untouched) ===== */}
      <footer style={{ background: '#1A3A30', color: '#D8E2DB' }}>
        <div className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '64px 32px 48px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 48, flexWrap: 'wrap', paddingBottom: 36, borderBottom: '1px solid rgba(216,226,219,0.18)' }}>
            <div style={{ maxWidth: 340 }}>
              <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, color: MC.bg, marginBottom: 12 }}>MyCIO<span style={{ color: MC.sage }}>.ai</span></div>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: '#A9BDB2', margin: 0 }}>{landing.footer.blurb}</p>
            </div>
            <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                <p style={{ fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6E8579', fontWeight: 700, margin: '0 0 3px' }}>{landing.footer.product}</p>
                <a href="#why" className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none' }}>{copy.nav.why}</a>
                <a href="#coverage" className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none' }}>{copy.nav.coverage}</a>
                <Link href="/your-data" className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none' }}>{yourData.navLabel}</Link>
                <a href="/#pricing" className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none' }}>{copy.nav.pricing}</a>
                <Link href="/sign-in" className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none' }}>{copy.nav.signin}</Link>
              </nav>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: 13, alignItems: 'flex-start' }}>
                <p style={{ fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6E8579', fontWeight: 700, margin: '0 0 3px' }}>{landing.footer.legal}</p>
                <a href="/#terms" onClick={() => setDocOpen('terms')} className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none', cursor: 'pointer' }}>{landing.footer.terms}</a>
                <a href="/#privacy" onClick={() => setDocOpen('privacy')} className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none', cursor: 'pointer' }}>{landing.footer.privacy}</a>
                <a href="/#refunds" onClick={() => setDocOpen('refund')} className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none', cursor: 'pointer' }}>{landing.footer.refund}</a>
                <a href="/#disclosures" onClick={() => setDocOpen('disclosures')} className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none', cursor: 'pointer' }}>{landing.footer.disclosures}</a>
                <a href="/#risk" onClick={() => setDocOpen('risk')} className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none', cursor: 'pointer' }}>{landing.footer.risk}</a>
              </nav>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                <p style={{ fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6E8579', fontWeight: 700, margin: '0 0 3px' }}>{copy.common.contact}</p>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="mc-foot-link" style={{ fontSize: 15, color: '#D8E2DB', textDecoration: 'none' }}>{SUPPORT_EMAIL}</a>
              </div>
            </div>
          </div>
          <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#80978C', margin: '28px 0 0', maxWidth: 860 }}>
            {landing.footer.disclosure} © 2026 Parsenne.{' '}
            <a href="/#disclosures" onClick={() => setDocOpen('disclosures')} style={{ fontSize: 12.5, color: MC.sage, cursor: 'pointer', textDecoration: 'underline' }}>{landing.footer.readDisclosures}</a>.
          </p>
        </div>
      </footer>

      {docOpen && <DocModal type={docOpen} onClose={closeDoc} />}
    </div>
  )
}
